/**
 * OFFLINE-FIRST ATTENDANCE (F1): general headcount + per-person check-in.
 *
 * Each entry uses a DETERMINISTIC id derived from `<session_id>:<person_id>`
 * (rendered as a stable UUID): two devices marking the same person OFFLINE
 * converge on the SAME row when reconciling (the write path upserts by id)
 * instead of duplicating the check-in. Design in docs/OFFLINE_ATTENDANCE.md.
 */

import { deterministicUuid } from "../utils/deterministic-id";

export type AttendanceStatus = "present" | "absent" | "late";

export interface AttendanceEntryLike {
  id: string;
  session_id: string | null;
  person_id: string | null;
  status: string | null;
  checked_in_at?: string | null;
}

/**
 * Stable id of a check-in (multi-device offline convergence). Native `uuid`
 * column: the logical key is hashed into a UUID, never stored as plain text.
 */
export function attendanceEntryId(sessionId: string, personId: string): string {
  return deterministicUuid(`attendance_entry:${sessionId}:${personId}`);
}

/** Counts as attendance? (`present` and `late` count; `absent` does not). */
export function countsAsPresent(status: string | null | undefined): boolean {
  return status === "present" || status === "late";
}

export interface AttendanceSummary {
  /** Check-ins with presence (present + late). */
  present: number;
  /** Explicitly marked absent. */
  absent: number;
  /** Total nominal entries. */
  entries: number;
  /**
   * Effective attendance: maximum between general headcount and nominal
   * check-ins — a quick count never loses people already marked.
   */
  effectiveTotal: number;
}

/** Pure summary of an attendance session. */
export function summarizeAttendance(
  entries: readonly AttendanceEntryLike[],
  headcount: number | null | undefined,
): AttendanceSummary {
  let present = 0;
  let absent = 0;
  for (const entry of entries) {
    if (countsAsPresent(entry.status)) {
      present += 1;
    } else if (entry.status === "absent") {
      absent += 1;
    }
  }
  return {
    present,
    absent,
    entries: entries.length,
    effectiveTotal: Math.max(headcount ?? 0, present),
  };
}

/** Status of a person in the roster (to render the check-in). */
export function personStatus(
  entries: readonly AttendanceEntryLike[],
  sessionId: string,
  personId: string,
): AttendanceStatus | null {
  const id = attendanceEntryId(sessionId, personId);
  const entry = entries.find(
    (e) => e.id === id || (e.session_id === sessionId && e.person_id === personId),
  );
  if (!entry) {
    return null;
  }
  return entry.status === "absent" ? "absent" : entry.status === "late" ? "late" : "present";
}
